import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useAuth } from "../lib/AuthContext";
import { useDemoMode } from "../lib/DemoModeContext";
import { supabase } from "../lib/supabase";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Alert, AlertDescription } from "../components/ui/alert";
import { 
  QrCode, 
  Camera, 
  Upload, 
  CheckCircle2, 
  Clock, 
  XCircle, 
  Shield, 
  Lock, 
  Eye,
  AlertCircle,
  ArrowRight,
  Leaf,
  Info
} from "lucide-react";
import { toast } from "sonner";

type VerificationStatus = "not_verified" | "pending" | "verified" | "expired" | "invalid";

interface VerificationData {
  status: VerificationStatus;
  clinic_name?: string;
  verified_at?: string;
  expires_at?: string;
  submitted_at?: string;
}

export default function PatientVerification() {
  const { user } = useAuth();
  const { 
    isDemoMode, 
    demoVerificationStatus, 
    demoClinicName, 
    demoVerifiedAt,
    simulateQRScan,
    validDemoTokens,
    expiredDemoTokens 
  } = useDemoMode();
  
  const [verificationData, setVerificationData] = useState<VerificationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [showScanner, setShowScanner] = useState(false);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [token, setToken] = useState("");
  const [tokenError, setTokenError] = useState("");
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showDemoInfo, setShowDemoInfo] = useState(false);

  useEffect(() => {
    if (user || isDemoMode) {
      fetchVerificationStatus();
    }
  }, [user, isDemoMode]);

  const fetchVerificationStatus = async () => {
    try {
      setLoading(true);
      
      // DEMO MODE: Use demo state instead of database
      if (isDemoMode) {
        console.log('🔧 DEMO MODE: Using demo verification status', demoVerificationStatus);
        
        let demoData: VerificationData = {
          status: demoVerificationStatus,
          clinic_name: demoClinicName || undefined,
          verified_at: demoVerifiedAt || undefined,
        };

        if (demoVerificationStatus === 'verified' && demoVerifiedAt) {
          const verifiedDate = new Date(demoVerifiedAt);
          const expiryDate = new Date(verifiedDate);
          expiryDate.setMonth(expiryDate.getMonth() + 12);
          demoData.expires_at = expiryDate.toISOString();
        }

        setVerificationData(demoData);
        setLoading(false);
        return;
      }

      // PRODUCTION MODE: Fetch from database
      const { data, error } = await supabase
        .from("patient_verifications")
        .select("*")
        .eq("user_id", user?.id)
        .order("created_at", { ascending: false })
        .limit(1)
        .single();

      if (error && error.code !== "PGRST116") {
        throw error;
      }

      if (data) {
        // Determine status based on data
        let status: VerificationStatus = "not_verified";
        
        if (data.verified_at) {
          // Check if verification has expired (12 months)
          const verifiedDate = new Date(data.verified_at);
          const expiryDate = new Date(verifiedDate);
          expiryDate.setMonth(expiryDate.getMonth() + 12);
          
          if (new Date() > expiryDate) {
            status = "expired";
          } else {
            status = "verified";
          }
        } else if (data.status === "pending") {
          status = "pending";
        }

        setVerificationData({
          status,
          clinic_name: data.clinic_name,
          verified_at: data.verified_at,
          expires_at: data.expires_at,
          submitted_at: data.created_at
        });
      } else {
        setVerificationData({ status: "not_verified" });
      }
    } catch (error) {
      console.error("Error fetching verification:", error);
      setVerificationData({ status: "not_verified" });
    } finally {
      setLoading(false);
    }
  };

  const handleTokenSubmit = async () => {
    // Validate token format (e.g., ABCD-1234)
    const tokenRegex = /^[A-Z0-9]{4}-[A-Z0-9]{4}$/;
    
    if (!token) {
      setTokenError("Please enter a verification token");
      return;
    }

    if (!tokenRegex.test(token.toUpperCase())) {
      setTokenError("Invalid token format. Expected format: ABCD-1234");
      return;
    }

    if (!consent) {
      setTokenError("Please accept the consent terms");
      return;
    }

    try {
      setSubmitting(true);
      setTokenError("");

      // DEMO MODE: Use simulated QR scan
      if (isDemoMode) {
        console.log('🔧 DEMO MODE: Simulating token validation', token);
        const result = await simulateQRScan(token);
        
        if (result.success) {
          toast.success(`✅ Verified with ${result.clinicName}!`);
          setShowManualEntry(false);
          setToken("");
          setConsent(false);
          fetchVerificationStatus();
        } else {
          setTokenError(result.error || "Verification failed");
        }
        setSubmitting(false);
        return;
      }

      // PRODUCTION MODE: Submit to database
      const { data, error } = await supabase
        .from("patient_verifications")
        .insert({
          user_id: user?.id,
          token: token.toUpperCase(),
          status: "pending",
          submitted_via: "manual"
        })
        .select()
        .single();

      if (error) throw error;

      toast.success("Verification submitted! Awaiting clinic confirmation.");
      setShowManualEntry(false);
      setToken("");
      setConsent(false);
      fetchVerificationStatus();
    } catch (error: any) {
      console.error("Error submitting token:", error);
      
      if (error.message?.includes("expired")) {
        setTokenError("This token has expired. Please request a new QR code from your clinic.");
      } else if (error.message?.includes("invalid")) {
        setTokenError("Invalid token. Please check the code and try again.");
      } else {
        setTokenError("Failed to submit verification. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleScannerOpen = () => {
    setShowScanner(true);
  };

  const handleQRUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // In a real implementation, you would:
    // 1. Upload the QR code image
    // 2. Parse the QR code using a library
    // 3. Extract the token
    // 4. Submit the token

    toast.info("QR scanning is being processed...");
    // For now, show manual entry as fallback
    setShowScanner(false);
    setShowManualEntry(true);
  };

  const cancelVerification = async () => {
    try {
      await supabase
        .from("patient_verifications")
        .update({ status: "cancelled" })
        .eq("user_id", user?.id)
        .eq("status", "pending");

      toast.success("Verification request cancelled");
      fetchVerificationStatus();
    } catch (error) {
      console.error("Error cancelling verification:", error);
      toast.error("Failed to cancel verification");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading verification status...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Demo Mode Banner */}
      {isDemoMode && (
        <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-white py-3 sticky top-0 z-50 shadow-lg">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Info className="w-5 h-5" />
                <div>
                  <span className="font-semibold">Demo Mode Active</span>
                  <span className="hidden sm:inline"> - Try the QR verification process with test tokens</span>
                </div>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setShowDemoInfo(true)}
                className="text-white hover:bg-white/20"
              >
                View Demo Tokens
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-gradient-to-br from-primary via-primary to-primary/90 text-primary-foreground pt-32 pb-16 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-10 w-64 h-64 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 left-10 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center"
          >
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 backdrop-blur-sm rounded-2xl mb-8">
              <Shield className="w-8 h-8" />
            </div>
            <h1 className="text-4xl sm:text-5xl mb-6">Patient Verification</h1>
            <p className="text-lg sm:text-xl opacity-90 leading-relaxed max-w-2xl mx-auto">
              Verify your patient status to unlock enhanced features and build community trust
            </p>
          </motion.div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Status Card */}
          {verificationData?.status === "not_verified" && <NotVerifiedCard onScan={handleScannerOpen} onManual={() => setShowManualEntry(true)} />}
          {verificationData?.status === "pending" && <PendingCard data={verificationData} onCancel={cancelVerification} />}
          {verificationData?.status === "verified" && <VerifiedCard data={verificationData} />}
          {verificationData?.status === "expired" && <ExpiredCard onReverify={() => setShowManualEntry(true)} />}
          {verificationData?.status === "invalid" && <InvalidCard onRetry={() => setShowManualEntry(true)} />}

          {/* Benefits Section */}
          <Card className="p-8 mt-8">
            <h2 className="text-2xl mb-6">Verification Benefits</h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              {/* Unverified Access */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Eye className="w-5 h-5 text-muted-foreground" />
                  <h3 className="text-lg">Without Verification</h3>
                </div>
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>Browse products and reviews</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>View general product information</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>Submit reviews (without verified badge)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>Access community discussions via discord</span>
                  </li>
                </ul>
              </div>

              {/* Verified Access */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <CheckCircle2 className="w-5 h-5 text-primary" />
                  <h3 className="text-lg">With Verification</h3>
                </div>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-primary mt-1" />
                    <span><strong>Verified Patient badge</strong> on all reviews</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-primary mt-1" />
                    <span><strong>Batch-level availability</strong> insights</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-primary mt-1" />
                    <span><strong>Enhanced community trust</strong> and credibility</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-primary mt-1" />
                    <span><strong>Product certificates</strong> (where available)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-primary mt-1" />
                    <span><strong>Priority support</strong> from CannaBias team</span>
                  </li>
                </ul>
              </div>
            </div>
          </Card>

          {/* Privacy Card */}
          <Card className="p-8 mt-8 bg-primary/5 border-primary/20">
            <div className="flex items-start gap-4">
              <Lock className="w-6 h-6 text-primary mt-1 flex-shrink-0" />
              <div>
                <h3 className="text-lg mb-3">Privacy-First Verification</h3>
                <p className="text-muted-foreground mb-4">
                  CannaBias uses a secure, privacy-by-design approach to patient verification:
                </p>
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <Shield className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                    <span><strong>No medical documents uploaded.</strong> Your clinic confirms verification using a secure QR token that expires in 24 hours.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Shield className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                    <span><strong>No personal data in tokens.</strong> QR codes contain only an encrypted verification ID.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Shield className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                    <span><strong>We only store verification status and timestamps.</strong> No medical records or prescription details.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Shield className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                    <span><strong>GDPR Article 6(a) & 9(2)(a) compliant.</strong> You control your data with full deletion rights.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Shield className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                    <span><strong>Verification renews every 12 months</strong> to ensure active patient status.</span>
                  </li>
                </ul>
                <div className="mt-4 pt-4 border-t border-border">
                  <p className="text-sm text-muted-foreground">
                    Read our{" "}
                    <button onClick={() => window.location.hash = "#/privacy"} className="text-primary hover:underline">Privacy Policy</button>
                    {" "}for more details.
                  </p>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Demo Info Dialog */}
      <Dialog open={showDemoInfo} onOpenChange={setShowDemoInfo}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Info className="w-5 h-5 text-orange-500" />
              Demo Mode - Test Tokens
            </DialogTitle>
            <DialogDescription>
              Use these test tokens to experience the QR verification process
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Valid Tokens */}
            <div>
              <h3 className="font-semibold mb-3 text-green-600 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" />
                Valid Tokens (Will verify successfully)
              </h3>
              <div className="space-y-2">
                {validDemoTokens.map((token, index) => {
                  const clinicNames = [
                    'Sapphire Medical Clinic',
                    'Curaleaf Clinic',
                    'Cannabis Clinic Cardiff',
                    'Medical Cannabis Clinics'
                  ];
                  return (
                    <div key={token} className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                      <div>
                        <code className="text-lg font-mono font-semibold text-green-700">{token}</code>
                        <p className="text-sm text-green-600 mt-1">{clinicNames[index]}</p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setToken(token);
                          setShowDemoInfo(false);
                          setShowManualEntry(true);
                        }}
                        className="border-green-300 text-green-700 hover:bg-green-100"
                      >
                        Use This
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Expired Tokens */}
            <div>
              <h3 className="font-semibold mb-3 text-orange-600 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Expired Tokens (Will show expiry error)
              </h3>
              <div className="space-y-2">
                {expiredDemoTokens.map((token) => (
                  <div key={token} className="flex items-center justify-between p-3 bg-orange-50 border border-orange-200 rounded-lg">
                    <code className="text-lg font-mono font-semibold text-orange-700">{token}</code>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setToken(token);
                        setShowDemoInfo(false);
                        setShowManualEntry(true);
                      }}
                      className="border-orange-300 text-orange-700 hover:bg-orange-100"
                    >
                      Use This
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            {/* Invalid Token Example */}
            <div>
              <h3 className="font-semibold mb-3 text-red-600 flex items-center gap-2">
                <XCircle className="w-4 h-4" />
                Invalid Tokens (Will show error)
              </h3>
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600 mb-2">Try any other token format to see error handling:</p>
                <code className="text-lg font-mono font-semibold text-red-700">FAKE-9999</code>
                <span className="text-sm text-red-600 ml-3">← Will show "Invalid token" error</span>
              </div>
            </div>

            <Alert>
              <AlertCircle className="w-4 h-4" />
              <AlertDescription>
                <strong>Demo Mode Note:</strong> In demo mode, verification is instant. In production, 
                clinic staff would need to approve verification requests, which may take a few minutes to hours.
              </AlertDescription>
            </Alert>
          </div>
        </DialogContent>
      </Dialog>

      {/* QR Scanner Modal */}
      <Dialog open={showScanner} onOpenChange={setShowScanner}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="w-5 h-5" />
              Scan Clinic QR Code
            </DialogTitle>
            <DialogDescription>
              Follow these steps to verify your patient status
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Steps */}
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
                  1
                </div>
                <div>
                  <p className="font-medium">Get QR code from your clinic</p>
                  <p className="text-sm text-muted-foreground">Ask your prescribing clinic for a CannaBias verification QR code</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
                  2
                </div>
                <div>
                  <p className="font-medium">Scan using camera or upload screenshot</p>
                  <p className="text-sm text-muted-foreground">Use your device camera or upload a photo of the QR code</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
                  3
                </div>
                <div>
                  <p className="font-medium">Confirmation in seconds</p>
                  <p className="text-sm text-muted-foreground">Your verification will be processed near-instantly</p>
                </div>
              </div>
            </div>

            {/* Token Expiry Notice */}
            <Alert>
              <Clock className="w-4 h-4" />
              <AlertDescription>
                QR codes expire 24 hours after generation for security. Request a new code if yours has expired.
              </AlertDescription>
            </Alert>

            {/* Upload Options */}
            <div className="space-y-3">
              <Label htmlFor="qr-upload" className="cursor-pointer">
                <div className="border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-primary hover:bg-primary/5 transition-colors">
                  <Upload className="w-8 h-8 mx-auto mb-3 text-muted-foreground" />
                  <p className="font-medium mb-1">Upload QR Code Image</p>
                  <p className="text-sm text-muted-foreground">Click to select or drag and drop</p>
                  <input
                    id="qr-upload"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleQRUpload}
                  />
                </div>
              </Label>

              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => {
                  setShowScanner(false);
                  setShowManualEntry(true);
                }}
              >
                Enter code manually instead
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Manual Entry Modal */}
      <Dialog open={showManualEntry} onOpenChange={setShowManualEntry}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Enter Verification Code</DialogTitle>
            <DialogDescription>
              Enter the alphanumeric code printed on your clinic's QR code
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="token">Verification Token</Label>
              <Input
                id="token"
                placeholder="ABCD-1234"
                value={token}
                onChange={(e) => {
                  setToken(e.target.value.toUpperCase());
                  setTokenError("");
                }}
                className="font-mono text-center text-lg tracking-wider"
                maxLength={9}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Format: ABCD-1234 (4 characters, hyphen, 4 characters)
              </p>
              {tokenError && (
                <p className="text-sm text-destructive mt-2 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {tokenError}
                </p>
              )}
            </div>

            <div className="flex items-start gap-2">
              <Checkbox
                id="consent"
                checked={consent}
                onCheckedChange={(checked) => setConsent(checked as boolean)}
              />
              <Label htmlFor="consent" className="text-sm leading-relaxed cursor-pointer">
                I confirm that CannaBias will only store my verification status and timestamps. 
                No personal medical data will be stored.
              </Label>
            </div>

            <Alert>
              <AlertCircle className="w-4 h-4" />
              <AlertDescription className="text-sm">
                If your token is invalid or expired, please contact your clinic for a new QR code.
              </AlertDescription>
            </Alert>

            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setShowManualEntry(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleTokenSubmit}
                disabled={submitting}
                className="flex-1"
              >
                {submitting ? "Submitting..." : "Submit Verification"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Status Card Components

function NotVerifiedCard({ onScan, onManual }: { onScan: () => void; onManual: () => void }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <Card className="p-8">
        <div className="text-center max-w-2xl mx-auto">
          <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mx-auto mb-6">
            <QrCode className="w-10 h-10 text-muted-foreground" />
          </div>
          
          <h2 className="text-2xl mb-3">Verify with Your Clinic QR Code</h2>
          <p className="text-muted-foreground mb-8">
            Get verified to unlock enhanced features and show your verified patient badge on reviews
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center mb-8">
            <Button size="lg" onClick={onScan} className="gap-2">
              <QrCode className="w-5 h-5" />
              Scan Clinic QR Code
            </Button>
            <Button size="lg" variant="outline" onClick={onManual} className="gap-2">
              Enter Code Manually
            </Button>
          </div>

          <p className="text-sm text-muted-foreground">
            You can continue leaving reviews without verification, but they will display without the Verified Patient badge.
          </p>
        </div>
      </Card>
    </motion.div>
  );
}

function PendingCard({ data, onCancel }: { data: VerificationData; onCancel: () => void }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <Card className="p-8 bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-900">
        <div className="text-center max-w-2xl mx-auto">
          <div className="w-20 h-20 rounded-full bg-amber-100 dark:bg-amber-900 flex items-center justify-center mx-auto mb-6">
            <Clock className="w-10 h-10 text-amber-600 dark:text-amber-400 animate-pulse" />
          </div>
          
          <Badge className="mb-4 bg-amber-500">Pending Verification</Badge>
          
          <h2 className="text-2xl mb-3">Awaiting Clinic Confirmation</h2>
          <p className="text-muted-foreground mb-6">
            Your verification request has been submitted and is awaiting confirmation from your clinic.
          </p>

          <div className="bg-background rounded-lg p-4 mb-6 text-left">
            <div className="flex justify-between mb-2">
              <span className="text-sm text-muted-foreground">Submitted:</span>
              <span className="text-sm">
                {data.submitted_at ? new Date(data.submitted_at).toLocaleDateString() : "Today"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Expected confirmation:</span>
              <span className="text-sm">Typically within minutes</span>
            </div>
          </div>

          <Button variant="outline" onClick={onCancel}>
            Cancel Request
          </Button>
        </div>
      </Card>
    </motion.div>
  );
}

function VerifiedCard({ data }: { data: VerificationData }) {
  const expiryDate = data.verified_at 
    ? new Date(new Date(data.verified_at).setMonth(new Date(data.verified_at).getMonth() + 12))
    : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <Card className="p-8 bg-primary/5 border-primary/20">
        <div className="text-center max-w-2xl mx-auto">
          <div className="w-20 h-20 rounded-full bg-primary flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-primary-foreground" />
          </div>
          
          <Badge className="mb-4 bg-primary">Verified Patient</Badge>
          
          <h2 className="text-2xl mb-3">You're Verified!</h2>
          <p className="text-muted-foreground mb-6">
            Your patient status has been verified. You now have access to enhanced features and your reviews display with the Verified Patient badge.
          </p>

          <div className="bg-background rounded-lg p-4 mb-6 text-left">
            {data.clinic_name && (
              <div className="flex justify-between mb-2">
                <span className="text-sm text-muted-foreground">Clinic:</span>
                <span className="text-sm">{data.clinic_name}</span>
              </div>
            )}
            <div className="flex justify-between mb-2">
              <span className="text-sm text-muted-foreground">Verified:</span>
              <span className="text-sm">
                {data.verified_at ? new Date(data.verified_at).toLocaleDateString() : "Recently"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Renewal required:</span>
              <span className="text-sm">
                {expiryDate ? expiryDate.toLocaleDateString() : "In 12 months"}
              </span>
            </div>
          </div>

          <Alert className="text-left">
            <Leaf className="w-4 h-4" />
            <AlertDescription>
              <strong>Enhanced Access Unlocked:</strong> You can now view THC/CBD potency data, batch-level availability insights, and your reviews display with the verified badge.
            </AlertDescription>
          </Alert>
        </div>
      </Card>
    </motion.div>
  );
}

function ExpiredCard({ onReverify }: { onReverify: () => void }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <Card className="p-8 bg-destructive/5 border-destructive/20">
        <div className="text-center max-w-2xl mx-auto">
          <div className="w-20 h-20 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-6">
            <XCircle className="w-10 h-10 text-destructive" />
          </div>
          
          <Badge variant="destructive" className="mb-4">Verification Expired</Badge>
          
          <h2 className="text-2xl mb-3">Verification Has Expired</h2>
          <p className="text-muted-foreground mb-6">
            Your patient verification has expired after 12 months. Request a new QR code from your clinic to reverify.
          </p>

          <Button onClick={onReverify} className="gap-2">
            Reverify Now
            <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </Card>
    </motion.div>
  );
}

function InvalidCard({ onRetry }: { onRetry: () => void }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <Card className="p-8 bg-destructive/5 border-destructive/20">
        <div className="text-center max-w-2xl mx-auto">
          <div className="w-20 h-20 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-10 h-10 text-destructive" />
          </div>
          
          <Badge variant="destructive" className="mb-4">Invalid Token</Badge>
          
          <h2 className="text-2xl mb-3">Verification Token Invalid</h2>
          <p className="text-muted-foreground mb-6">
            The verification token you provided is invalid or has expired. Please contact your clinic for a new QR code.
          </p>

          <div className="space-y-3">
            <Button onClick={onRetry} className="gap-2 w-full sm:w-auto">
              Try Again
              <ArrowRight className="w-4 h-4" />
            </Button>
            <p className="text-sm text-muted-foreground">
              Need help?{" "}
              <button className="text-primary hover:underline">
                Contact Support
              </button>
            </p>
          </div>
        </div>
      </Card>
    </motion.div>
  );
}